import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from "react"
import type { AppDispatch } from "@core/store"
import { io, Socket } from "socket.io-client"
import { useDispatch, useSelector } from "react-redux"
import { getCurrentServerUrls } from "@core/utils/serverConfig"
import { handleReferenceUpdate } from "@core/utils/referenceDataUpdater"
import { getUserInfoFromToken, isLoggedIn } from "@core/utils/tokenValidator"
import { isAuthStorageEventKey } from "@core/utils/webSessionAuth"

import { selectedDateSelector } from "@/features/selectedDate/selectedDateSelectors"
import { fetchServerInfo } from "@/features/hospital/hospitalSlice"

import { waitingVisit, treatmentVisit, completeVisit } from "@/features/visit/visitSlice"
import { reservationFetchThunk } from "@core/api/reservationApi"
import { handleSioUpdate } from "@core/utils/sioDataUpdater"
import type { SocketState } from "./SocketContext.types"
import { attachPatientListRefresh } from "./patientListRefreshSocket"

function createSocket(wsUrl: string | null): Socket | null {
  if (!wsUrl) return null

  return io(wsUrl, {
    path: "/socket.io",
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    transports: ["websocket"],
    upgrade: false,
    withCredentials: true,
  })
}

const ElectronSocketContext = createContext<SocketState | null>(null)

const isNativeElectronRuntime = () => typeof window !== "undefined" && Boolean(window.electronAPI)

export const ElectronSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch()
  const selectedDate = useSelector(selectedDateSelector)
  const selectedDateRef = useRef(selectedDate)
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const previousAuthStateRef = useRef(isLoggedIn())
  const previousMemberUuidRef = useRef(getUserInfoFromToken()?.memberUuid || null)
  const allowUnauthenticatedSocketRef = useRef(isNativeElectronRuntime())
  const [reconnectKey, setReconnectKey] = useState(0)

  const reconnect = useCallback(() => {
    setConnected(false)
    setError(false)
    setReconnectKey(prev => prev + 1)
  }, [])

  useEffect(() => {
    allowUnauthenticatedSocketRef.current = isNativeElectronRuntime()
    selectedDateRef.current = selectedDate
  }, [selectedDate])

  const handleConnect = () => {
    setConnected(true)
    setError(false)

    if (!isLoggedIn()) {
      return
    }

    void (async () => {
      try {
        const tasks = [dispatch(fetchServerInfo())]
        await Promise.allSettled(tasks)
      } catch (e) {
        console.warn("[ElectronSocketContext] 초기 서버 정보 로드 실패(격리 처리)", e)
      }
    })()
  }

  const handleDisconnect = (reason: string) => {
    setConnected(false)
    if (reason === "io client disconnect") {
      return
    }

    console.debug("[ElectronSocketContext] 소켓 연결 끊김", {
      reason,
      socketId: socketRef.current?.id,
    })
    setConnected(false)
    setError(true)
  }

  const handleConnectError = async (connectError: any) => {
    console.error("[ElectronSocketContext] 소켓 연결 오류", connectError)
    setConnected(false)
    setError(true)
  }

  useEffect(() => {
    let patientRefresh: ReturnType<typeof attachPatientListRefresh> | null = null
    const isReceptionOrConsentScreen = () => {
      const path = window.location.pathname + window.location.hash
      return path.includes("/android/reception/") || path.includes("/android/consent") || path.includes("/android/no-chart-consent")
    }

    const handleVisitUpdate = (data: string | { visitStatus: string }) => {
      if (patientRefresh?.isActive()) {
        console.warn("[ElectronSocketContext] 활성 협상에 기존 목록 알림 도착")
        return
      }
      if (!isLoggedIn()) {
        console.debug("[ElectronSocketContext] 로그인 전 방문 상태 브로드캐스트 무시")
        return
      }
      if (isReceptionOrConsentScreen()) {
        console.debug("[ElectronSocketContext] 접수/동의서 화면 — 방문 상태 브로드캐스트 무시")
        return
      }
      const currentSelectedDate = selectedDateRef.current
      const visitStatus = typeof data === "string" ? data : data.visitStatus
      console.log("방문 상태 업데이트 수신:", visitStatus)
      console.log("선택된 날짜:", currentSelectedDate)

      switch (visitStatus) {
        case "reservation":
          dispatch(reservationFetchThunk(currentSelectedDate))
          break
        case "waiting":
          dispatch(waitingVisit.fetchList(currentSelectedDate))
          break
        case "treatment":
          dispatch(treatmentVisit.fetchList(currentSelectedDate))
          break
        case "complete":
          dispatch(completeVisit.fetchList(currentSelectedDate))
          break
      }
    }

    const handleReferenceUpdateWrapper = async (data: { reference: string }) => {
      if (!isLoggedIn()) {
        console.debug("[ElectronSocketContext] 로그인 전 참조 브로드캐스트 무시")
        return
      }
      if (isReceptionOrConsentScreen()) {
        console.debug("[ElectronSocketContext] 접수/동의서 화면 — 참조 브로드캐스트 무시")
        return
      }
      await handleReferenceUpdate(data.reference)
    }

    const handleSioUpdateWrapper = async (data: any) => {
      if (!isLoggedIn()) {
        console.debug("[ElectronSocketContext] 로그인 전 액션 브로드캐스트 무시")
        return
      }
      if (isReceptionOrConsentScreen()) {
        console.debug("[ElectronSocketContext] 접수/동의서 화면 — 액션 브로드캐스트 무시")
        return
      }
      console.debug("액션 데이터 업데이트 수신:", data)
      await handleSioUpdate(data)
    }

    let cleanup: (() => void) | null = null

    const initializeSocket = async () => {
      try {
        const urls = getCurrentServerUrls()

        if (!urls.WS_BASE_URL) {
          console.warn("[ElectronSocketContext] 부트스트랩 서버 설정이 없습니다. 연결 오류 상태로 설정합니다.")
          setConnected(false)
          setError(true)
          return
        }

        const socket = createSocket(urls.WS_BASE_URL)
        if (!socket) {
          console.error("[ElectronSocketContext] 소켓 인스턴스 생성 실패")
          setConnected(false)
          setError(true)
          return
        }

        socketRef.current = socket

        socket.on("connect", handleConnect)
        socket.on("disconnect", handleDisconnect)
        socket.on("connect_error", handleConnectError)
        socket.on("visit_update", handleVisitUpdate)
        socket.on("reference_update", handleReferenceUpdateWrapper)
        socket.on("action_update", handleSioUpdateWrapper)
        patientRefresh = attachPatientListRefresh(socket, () => isLoggedIn() && !isReceptionOrConsentScreen())

        if (allowUnauthenticatedSocketRef.current || isLoggedIn()) {
          socket.connect()
        } else {
          setConnected(false)
          setError(false)
        }

        cleanup = () => {
          patientRefresh?.dispose()
          if (socket) {
            socket.off("connect", handleConnect)
            socket.off("disconnect", handleDisconnect)
            socket.off("connect_error", handleConnectError)
            socket.off("visit_update", handleVisitUpdate)
            socket.off("reference_update", handleReferenceUpdateWrapper)
            socket.off("action_update", handleSioUpdateWrapper)
            socket.disconnect()
          }
        }
      } catch (initializeError) {
        console.error("[ElectronSocketContext] 소켓 초기화 중 오류", initializeError)
        setConnected(false)
        setError(true)
      }
    }

    initializeSocket()

    return () => {
      if (cleanup) cleanup()
    }
  }, [dispatch, reconnectKey])

  useEffect(() => {
    const handleAuthStorageChange = (event: Event) => {
      const storageEvent = event as StorageEvent

      if (typeof storageEvent.key !== "undefined" && !isAuthStorageEventKey(storageEvent.key)) {
        return
      }

      const socket = socketRef.current
      if (!socket) {
        return
      }

      const nextIsLoggedIn = isLoggedIn()
      const previousIsLoggedIn = previousAuthStateRef.current
      previousAuthStateRef.current = nextIsLoggedIn
      const nextMemberUuid = getUserInfoFromToken()?.memberUuid || null
      const previousMemberUuid = previousMemberUuidRef.current
      previousMemberUuidRef.current = nextMemberUuid
      const shouldKeepSocketConnected = allowUnauthenticatedSocketRef.current || nextIsLoggedIn

      if (nextIsLoggedIn !== previousIsLoggedIn || nextMemberUuid !== previousMemberUuid) {
        socket.disconnect()

        if (shouldKeepSocketConnected) {
          setConnected(false)
          setError(false)
          socket.connect()
        } else {
          setConnected(false)
          setError(false)
        }

        return
      }

      if (!socket.connected && socket.active && shouldKeepSocketConnected) {
        setError(false)
        socket.connect()
      }
    }

    window.addEventListener("storage", handleAuthStorageChange)

    return () => {
      window.removeEventListener("storage", handleAuthStorageChange)
    }
  }, [])

  return (
    <ElectronSocketContext.Provider
      value={{
        socket: socketRef.current,
        connected,
        available: connected,
        limitedMode: false,
        error,
        reconnect,
      }}
    >
      {children}
    </ElectronSocketContext.Provider>
  )
}

export const useElectronSocket = (): SocketState => {
  const ctx = useContext(ElectronSocketContext)
  if (!ctx) {
    console.warn("ElectronSocketContext가 없습니다. 기본 연결 오류 상태를 반환합니다.")
    return {
      socket: null,
      connected: false,
      available: false,
      limitedMode: false,
      error: true,
      reconnect: () => {},
    }
  }
  return ctx
}
